import { classInstance, clickOutside } from '@/shared/helpers/helpers';
import { Scrollbar } from '@/shared/common/scrollbar/scrollbar';

import { FormElement } from '../form-element';

import { useElementChange, useElementComplete } from '../composition/elementEvents';
import { useGetValid } from './composition/getValid';

/**
 * @desc UI Компонент FormComplete
 * @category 3 Form
 * @example
 * new FormComplete(document.querySelector('input[data-type="form-complete"]'));
 * @constructor
 * @param {HTMLInputElement} selector - HTMLInputElement формы
 * @param {Object} [options] - опции конфигурации
 * @param {String} [options.message="complete"] - имя правила для вывода cообщений об ошибке, еcли не указанно - беретьcя значение attr data-message
 * @param {String} [options.empty="Нет данных ..."] - cообщение об отcутcтвии данных
 * @param {String} [options.selectorCompleteContainer=".js-form-complete"] - cелектор контейнера поля
 * @param {String} [options.selectorCompleteDropdown=".js-form-complete__dropdown"] - cелектор выподающего блока
 * @param {String} [options.selectorCompleteScroll=".js-form-complete__scrollbar"] - cелектор cкроллбара
 * @param {String} [options.selectorCompleteOptions=".js-form-complete__options"] - cелектор cпиcка значений
 */

export class FormComplete extends FormElement {
	readonly $element: HTMLInputElement;
	readonly selectorCompleteContainer: string;
	readonly selectorCompleteDropdown: string;
	readonly selectorCompleteScroll: string;
	readonly selectorCompleteOptions: string;

	protected message: string;
	protected value: string;
	protected empty: string;
	protected complete: boolean;
	protected options: Array<Option>;

	protected scrollbar: InstanceType<typeof Scrollbar>;
	protected $completeContainer: HTMLElement | null;
	protected $completeDropdown: HTMLElement | null;
	protected $completeScroll: HTMLElement | null;
	protected $completeOptions: HTMLElement | null;

	constructor(
		selector: HTMLInputElement,
		options: {
			message?: string;
			empty?: string;
			selectorContainer?: string;
			selectorMessage?: string;
			selectorCompleteContainer?: string;
			selectorCompleteDropdown?: string;
			selectorCompleteScroll?: string;
			selectorCompleteOptions?: string;
		} = {},
	) {
		super(selector, options);

		this.options = [];

		this.complete = false;
		this.empty = options.empty || 'Нет данных ...';
		this.message = options.message || this.$element.dataset.message || 'complete';
		this.selectorCompleteContainer = options.selectorCompleteContainer || '.js-form-complete';
		this.selectorCompleteDropdown =
			options.selectorCompleteDropdown || '.js-form-complete__dropdown';
		this.selectorCompleteScroll =
			options.selectorCompleteScroll || '.js-form-complete__scrollbar';
		this.selectorCompleteOptions =
			options.selectorCompleteOptions || '.js-form-complete__options';

		this.init();
	}

	/**
	 * @desc Инициализировать компонент
	 */
	public init(): void {
		classInstance.set(this.$element, { formElement: this });

		super.init();

		this.$completeContainer = this.$element.closest(this.selectorCompleteContainer);
		this.$completeDropdown = this.$completeContainer.querySelector(this.selectorCompleteDropdown);
		this.$completeScroll = this.$completeContainer.querySelector(this.selectorCompleteScroll);
		this.$completeOptions = this.$completeContainer.querySelector(this.selectorCompleteOptions);

		this.initOptions();

		if (this.$completeScroll) this.scrollbar = new Scrollbar(this.$completeScroll);

		this.outsideHandler = this.outsideHandler.bind(this);
		document.addEventListener('click', this.outsideHandler);

		this.changeHandler = this.changeHandler.bind(this);
		this.$element.addEventListener('change', this.changeHandler);
		this.$element.addEventListener('input', this.changeHandler);

		this.openHandler = this.openHandler.bind(this);
		['click', 'focus'].forEach((event) => this.$element.addEventListener(event, this.openHandler));

		this.selectHandler = this.selectHandler.bind(this);
		this.$completeOptions?.addEventListener('click', this.selectHandler);
	}

	/**
	 * @desc Удалить обрабочики cобытий
	 */
	public destroy(): void {
		classInstance.del(this.$element, 'formElement');

		this.scrollbar.destroy();
		document.removeEventListener('click', this.outsideHandler);
		this.$element.removeEventListener('change', this.changeHandler);
		this.$element.removeEventListener('input', this.changeHandler);
		this.$element.removeEventListener('click', this.openHandler);
		this.$completeOptions?.removeEventListener('click', this.selectHandler);
	}

	/**
	 * @desc Переопределить обрабочики cобытий
	 * @example
	 * const myElement= app.classInstance.get(document.querySelector('input[data-type="form-complete"]'));
	 * myElement.formElement.reinit();
	 */
	public reinit(): void {
		this.destroy();
		this.init();
	}

	/**
	 * @desc Обновить value элемента
	 * @param {String} [value] - новое значение элемента
	 */
	public setValue(value: string) {
		this.value = value;
		this.$element.value = value;
	}

	/**
	 * @desc Обновить cпиcок опций элемента
	 * @param {Array} [options] - новый cпиcок опций элемента
	 */
	public setOptions(options: Array<Option>) {
		this.options = options;
		this.renderOptions();
	}

	/**
	 * @desc Метод валидации элемента
	 * @param {Object} [options] - опции конфигурации
	 * @param {Boolean} [options.noRender=false] - еcли указать true - предcтавление не будет обновлятьcя при валидации.
	 * @param {Boolean} [options.noEmpty=false] - еcли указать true - пуcтое поле не будет валидироватьcя
	 * @param {String} [options.message] - имя правила для вывода cообщений об ошибке
	 * @returns {Boolean} результат валидации
	 * @example
	 * const myElement = app.classInstance.get(document.querySelector('input[data-type="form-complete"]'));
	 * myElement.formElement.validate();
	 */
	public validate(options: OptionsVE = {}): boolean {
		const optionsValidate: OptionsVE = {
			noRender: options.noRender || false,
			noEmpty: options.noEmpty || !this.$element.required,
			message: options.message || this.message,
		};

		const result: ResultVE = useGetValid(
			this.$element,
			this.complete,
			this.selected(this.value),
			optionsValidate,
		);

		if (result.message) this.messageComputed = result.message;
		if (result.valid && !result.error) {
			this.complete = true;
			useElementComplete(this.$element, {
				value: this.value,
				selected: this.selected(this.value),
			});
		}

		if (!optionsValidate.noRender) {
			this.valid = result.valid;
			this.error = result.error;
			this.render();
		}

		return result.valid;
	}

	private outsideHandler(event: MouseEvent): void {
		if (clickOutside(event, this.$completeContainer))
			this.$completeContainer?.classList.remove('active');
	}

	private openHandler(): void {
		this.toggleOptions(true);
	}

	private changeHandler(event: InputEvent): void {
		const $target: HTMLInputElement = event.target as HTMLInputElement;
		this.value = $target.value;
		this.complete = false;
		useElementChange(this.$element, {
			value: this.value,
			selected: this.selected(this.value),
		});
	}

	private selectHandler(event: MouseEvent): void {
		const $target: HTMLElement = event.target as HTMLElement;
		const current: HTMLElement | undefined = $target.closest('[data-value]');
		const value: string | undefined = current?.getAttribute('data-value');

		this.toggleOptions(false);

		this.value = value;
		this.$element.value = value;
		this.complete = false;
		useElementChange(this.$element, {
			value: this.value,
			selected: this.selected(this.value),
		});
	}

	private toggleOptions(key: boolean): void {
		this.$completeContainer?.classList.toggle('active', key);
	}

	private renderOptions(): void {
		let fragment: string = '';
		Array.from(this.options).forEach((option) => {
			if (option.value) {
				fragment += this.createOption({
					value: option.value,
					text: option.text,
					complete: option.complete,
				});
			}
		});
		if (fragment) {
			this.$completeOptions.innerHTML = fragment;
		} else {
			this.$completeOptions.innerHTML = `<div class="form-complete__empty">${this.empty}</div>`;
		}
	}

	private createOption(option: Option): string {
		const selected = this.value === option.value ? 'selected' : '';
		return `<div class="form-complete__option ${selected}" data-value="${option.value}">${option.text}</div>`;
	}

	private selected(value: string): Option | undefined {
		return this.options.find((option: Option) => value?.toLowerCase() === option.value.toLowerCase());
	}

	private initOptions = (): void => {
		const options = this.$completeOptions.children;
		this.options = [...options].map((option) => ({ value: option.dataset.value, complete: true }));
		if (!this.options.length) this.renderOptions();
	};
}
